import './ContentStatistics.scss';

import React, { Fragment } from 'react';

import HelpIcon from '@mui/icons-material/Help';
import { Grid, Tooltip } from '@mui/material';

export interface IStatistics {
    sent: number;
    errored: number;
    received: number;
    opened: number;
    deleted: number;
}

export interface IPlatformStatistics {
  iOS: IStatistics;
  Android: IStatistics;
}

export interface IStatisticsReply {
  statusCountTypeId: number;
  appReplyTypeId: number;
  count: number;
  lastUpdateDate: string;
}

export interface IStatisticsNotification {
  statusCountTypeId: number;
  success: number;
  error: number;
  lastUpdateDate: string;
}

export interface ICampaignStatistics {
  id: string;
  recordGuid: string;
  createDate: string;
  appReplyStatusCounts: Array<IStatisticsReply>;
  notificationStatusCounts: Array<IStatisticsNotification>;
}

export interface ICampaignStatisticsProps {
  platformStatistics: IPlatformStatistics | undefined;
}

export default function CampaignStatistics(props: ICampaignStatisticsProps) {

  const { platformStatistics } = props;
  let numberOfDefaultStats = 0;

  interface IArrayEntry {
    text: string;
    helpText: string | null;
    className: string | null;
  }

  function getHelp(helpText: string | null) {
    let help = <Fragment />;
    if (helpText) {
      help = <Tooltip arrow title={helpText}>
              <HelpIcon className="small"/>
             </Tooltip>;
    }
    return help;
  }

  interface IRowProps {
    entries: Array<IArrayEntry>;
  }

  function Row(props: IRowProps) {
    const { entries } = props;
    return (
      <Fragment>
        <Grid item xs={4}>
          <div className={entries[0].className ? entries[0].className : "default"}>
            {entries[0].text}
            {getHelp(entries[0].helpText)}
          </div>
        </Grid>
        <Grid item xs={4}>
          <div className={entries[1].className ? entries[1].className : "default"}>
            {entries[1].text}
            {getHelp(entries[1].helpText)}
          </div>
        </Grid>
        <Grid item xs={4}>
          <div className={entries[2].className ? entries[2].className : "default"}>
            {entries[2].text}
            {getHelp(entries[2].helpText)}
          </div>
        </Grid>
      </Fragment>
    )
  }

  function getTextValue(value: any) : string {
    const defaultValue = "0*";
    if (value === undefined || value === null) {
      numberOfDefaultStats++;
      return defaultValue;
    }
    return '' + value;
  }

  const sentHelp="This indicates how many campaign messages the notification server believes it delivered within the first 5 minutes after the send was initiated.";
  const errorHelp="This indicates how many campaign messages the notification server received a fatal error on delivery within the first 5 minutes after the send was initiated.";
  const receivedHelp="This indicates how many actual replies have been received from the application installed on devices indicating a notification arrived on the device.";
  const openedHelp="This indicates how many actual replies have been received from the application installed on devices indicating a notification has been opened by the user on the device.";
  const deletedHelp="This indicates how many actual replies have been received from the application installed on devices indicating a notification has been deleted by the user on the device.";


  const dataRowNotificationsHeader: Array<IArrayEntry> = [
    {text: 'Notifications', helpText: null, className: "bold"},
    {text: 'iOS', helpText: null, className: "rightAlign bold"},
    {text: 'Android', helpText: null, className: "rightAlign bold"}
  ];

  const dataRowSent: Array<IArrayEntry> = [
    {text: 'Sent:', helpText: sentHelp, className: null},
    {text: getTextValue(platformStatistics?.iOS.sent), helpText: null, className: "rightAlign"},
    {text: getTextValue(platformStatistics?.Android.sent), helpText: null, className: "rightAlign"}
  ];

  const dataRowErrored: Array<IArrayEntry> = [
    {text: 'Errored:', helpText: errorHelp, className: null},
    {text: getTextValue(platformStatistics?.iOS.errored), helpText: null, className: "rightAlign"},
    {text: getTextValue(platformStatistics?.Android.errored), helpText: null, className: "rightAlign"}
  ];

  const dataRowRepliesHeader: Array<IArrayEntry> = [
    {text: 'Replies', helpText: null, className: "bold"},
    {text: 'iOS', helpText: null, className: "rightAlign bold"},
    {text: 'Android', helpText: null, className: "rightAlign bold"}
  ];

  const dataRowReceived: Array<IArrayEntry> = [
    {text: 'Received:', helpText: receivedHelp, className: null},
    {text: getTextValue(platformStatistics?.iOS.received), helpText: null, className: "rightAlign"},
    {text: getTextValue(platformStatistics?.Android.received), helpText: null, className: "rightAlign"}
  ];

  const dataRowOpened: Array<IArrayEntry> = [
    {text: 'Opened:', helpText: openedHelp, className: null},
    {text: getTextValue(platformStatistics?.iOS.opened), helpText: null, className: "rightAlign"},
    {text: getTextValue(platformStatistics?.Android.opened), helpText: null, className: "rightAlign"}
  ];

  const dataRowDeleted: Array<IArrayEntry> = [
    {text: 'Deleted:', helpText: deletedHelp, className: null},
    {text: getTextValue(platformStatistics?.iOS.deleted), helpText: null, className: "rightAlign"},
    {text: getTextValue(platformStatistics?.Android.deleted), helpText: null, className: "rightAlign"}
  ];

  let note = numberOfDefaultStats > 0 ? (
      <div className="stats-note">
      * Stats require 2 hours to be generated.
      </div>
    ) : (
      <Fragment></Fragment>
    );

  return (
    <Fragment>
        <Grid container spacing={1}>
          <Grid container item xs={12} spacing={1}>
            <Row entries={dataRowNotificationsHeader}  />
            <Row entries={dataRowSent}  />
            <Row entries={dataRowErrored} />
          </Grid>
        </Grid>
        <hr className="hr" />
        <Grid container spacing={1}>
          <Grid container item xs={12} spacing={1}>
            <Row entries={dataRowRepliesHeader} />
            <Row entries={dataRowReceived} />
            <Row entries={dataRowOpened} />
            <Row entries={dataRowDeleted} />
          </Grid>
        </Grid>
        {note}
        <br />
    </Fragment>
  );
};
